import type { ExternalDependencies } from '@atlassian/relay-fetch/multi-fetch';
import { captureException } from '@sentry/react';
import type { PayloadError, RequestParameters, Variables } from 'relay-runtime';

import { GraphQLFetchError, RequestError } from '@townsquare/error-classes';

export const captureErrorSentry = ({
	fetchUrl,
	request,
	variables,
	error,
	extras = {},
}: {
	fetchUrl: string;
	request: RequestParameters;
	variables: Variables;
	error: RequestError;
	extras: Record<string, unknown>;
}): void => {
	captureException(error, (scope) =>
		scope
			.setExtras({
				...request,
				...extras,
				requestVariables: variables,
				graphQLUrl: fetchUrl,
			})
			.setTags({
				operationName: request.name,
			})
			.setFingerprint([request.name, String(error.statusCode)]),
	);
};

type PayloadErrorWithExtensions = PayloadError & {
	extensions?: { statusCode?: number };
};

type GetLoggingAndMetricsHelper = NonNullable<ExternalDependencies['getLoggingAndMetricsHelper']>;

export const getAggLoggingAndMetricsHelper: GetLoggingAndMetricsHelper = ({
	queryName,
	operationKind,
}) => ({
	logNonBreakingErrors: (errors, requestId) => {
		if (!errors || errors.length === 0) {
			return;
		}

		const messages = errors.reduce<Record<string, unknown>>((acc, error, index) => {
			acc[`message[${index}]`] = error.message;
			return acc;
		}, {});

		const firstError = errors[0] as PayloadErrorWithExtensions;
		const error = new GraphQLFetchError(firstError.message, firstError.extensions?.statusCode);

		captureException(error, (scope) =>
			scope
				.setExtras({
					...messages,
					operationKind,
					...(requestId !== undefined ? { requestId } : {}),
				})
				.setTags({ operationName: queryName })
				.setFingerprint([queryName, String(error.statusCode)]),
		);
	},
	// The same error is always forwarded to `sink.error` immediately after this
	// hook fires (see multi-fetch.ts), so a render-path error boundary reports it
	// to Sentry; reporting here too would double-count. Matches the Status env,
	// which also does not report transport errors from its network layer.
	logError: () => {},
	logErrorRetry: (error, retryAttempt) => {
		if (!error) {
			return;
		}

		captureException(error, (scope) =>
			scope
				.setLevel('info')
				.setExtras({ operationKind, retryAttempt })
				.setTags({ operationName: queryName }),
		);
	},
});
